import { createSlice } from "@reduxjs/toolkit";
import APIService from "../services/api.service";
import { AppDispatch } from "../store";

const initialState = {
  stakeholderDialogOpen: false,
  stakeholderId: null,
  stakeholderProfile: {
    name: '',
    comment: '',
    levelOfPower: -1,
    levelOfSupport: -1,
    levelOfSupportVerified: false,
    confidenceIndice: 1,
    howImpacted: '',
    benefits: [],
    collateralDamages: ''
  },
  stakeholderLevers: {
    aspirations: [],
    resistances: []
  },
  // Index of the resistance whose tactical-actions panel is open, or null.
  activeResistanceIndex: null as number | null,
  stakeholdersInfluencings: [],
  stakeholderIsLoading: false,
  stakeholderStatus: null,
  stakeholderNextAction: null,
  isEditabled: true,
  isDuplicated: false,
};

export const stakeholder = createSlice({
  name: "stakeholder",
  initialState,
  reducers: {
    openStakeholderDialog: (state, action) => {
      // Reinitialize
      state.stakeholderId = null;
      state.stakeholderStatus = null;
      state.stakeholderId = null;
      // Profile
      state.stakeholderProfile.name = '';
      state.stakeholderProfile.comment = '';
      state.stakeholderProfile.levelOfPower = -1;
      state.stakeholderProfile.levelOfSupport = -1;
      state.stakeholderProfile.levelOfSupportVerified = false;
      state.stakeholderProfile.confidenceIndice = 1;
      state.stakeholderProfile.howImpacted = '';
      state.stakeholderProfile.benefits = [];
      state.stakeholderProfile.collateralDamages = '';
      // Levers
      state.stakeholderLevers.aspirations = [];
      state.stakeholderLevers.resistances = [];
      state.activeResistanceIndex = null;
      // Influencings
      state.stakeholdersInfluencings = [];

      state.stakeholderDialogOpen = true;
      if (action.payload.element.id) {
        // Element
        if (action.payload.isDuplicated === true) {
          state.stakeholderId = null;
          state.stakeholderProfile.name = `${action.payload.element.name} - copy`;
        } else {
          state.stakeholderId = action.payload.element.id;
          state.stakeholderProfile.name = action.payload.element.name;
        }
        // Profile
        state.stakeholderProfile.comment = action.payload.element.comment;
        state.stakeholderProfile.levelOfPower = action.payload.element.level_of_power;
        state.stakeholderProfile.levelOfSupport = action.payload.element.level_of_support;
        state.stakeholderProfile.levelOfSupportVerified = action.payload.element.level_of_support_verified;
        state.stakeholderProfile.confidenceIndice = action.payload.element.confidence_indice;
        state.stakeholderProfile.howImpacted = action.payload.element.how_impacted;
        state.stakeholderProfile.benefits = action.payload.element.benefits;
        state.stakeholderProfile.collateralDamages = action.payload.element.collateral_damages;
        // Levers
        state.stakeholderLevers.aspirations = action.payload.element.aspirations;
        state.stakeholderLevers.resistances = action.payload.element.resistances;
        // Influencings
        state.stakeholdersInfluencings = action.payload.element.influencings;
        // Other
        state.isEditabled = action.payload.isEditabled;
        state.isDuplicated = action.payload.isDuplicated;
      }
    },
    closeStakeholderDialog: (state) => {
      state.stakeholderDialogOpen = false;
    },
    loadingStakeholder: (state) => {
      state.stakeholderIsLoading = true;
    },
    resetStakeholder: (state, action) => {
      state.stakeholderIsLoading = false;
      state.stakeholderStatus = 'success';
      state.stakeholderNextAction = action.payload;
    },
    resetStakeholderStatus: (state) => {
      state.stakeholderId = null;
      state.stakeholderStatus = null;
      state.stakeholderId = null;
      // Profile
      state.stakeholderProfile.name = '';
      state.stakeholderProfile.comment = '';
      state.stakeholderProfile.levelOfPower = 5;
      state.stakeholderProfile.levelOfSupport = 5;
      state.stakeholderProfile.levelOfSupportVerified = false;
      state.stakeholderProfile.confidenceIndice = 1;
      state.stakeholderProfile.howImpacted = '';
      state.stakeholderProfile.benefits = [];
      state.stakeholderProfile.collateralDamages = '';
      // Levers
      state.stakeholderLevers.aspirations = [];
      state.stakeholderLevers.resistances = [];
      state.activeResistanceIndex = null;
      // Influencings
      state.stakeholdersInfluencings = [];
    },
    // --- Tactical actions attached to a resistance -----------------------
    // The picker panel is shared by all resistance rows, so the slice tracks
    // which row is being edited; the moves themselves live on that row.
    openResistanceMovesPanel: (state, action) => {
      state.activeResistanceIndex = action.payload;
    },
    closeResistanceMovesPanel: (state) => {
      state.activeResistanceIndex = null;
    },
    toggleResistanceMove: (state, action) => {
      const index = state.activeResistanceIndex;
      const row = index === null ? null : state.stakeholderLevers.resistances[index];
      if (!row) {
        return;
      }
      const current = row.selectedMoves || [];
      row.selectedMoves = current.some((m) => m.id === action.payload.id)
        ? current.filter((m) => m.id !== action.payload.id)
        : [...current, { id: action.payload.id, name: action.payload.name }];
    },
    removeResistanceMove: (state, action) => {
      const row = state.stakeholderLevers.resistances[action.payload.index];
      if (!row) {
        return;
      }
      row.selectedMoves = (row.selectedMoves || []).filter((m) => m.id !== action.payload.moveId);
    },
    clearResistanceMoves: (state, action) => {
      const row = state.stakeholderLevers.resistances[action.payload];
      if (row) {
        row.selectedMoves = [];
      }
    },
    updateField: (state, action) => {
      if (action.payload.field === null) {
        state[action.payload.parentField] = action.payload.value;
      } else {
        state[action.payload.parentField][action.payload.field] = action.payload.value;
      }
    }
  },
});

export const {
  openStakeholderDialog,
  closeStakeholderDialog,
  loadingStakeholder,
  resetStakeholder,
  resetStakeholderStatus,
  updateField,
  openResistanceMovesPanel,
  closeResistanceMovesPanel,
  toggleResistanceMove,
  removeResistanceMove,
  clearResistanceMoves
} = stakeholder.actions;

export default stakeholder.reducer;

export const stakeholderPost = (nextAction, data: any, id: any = null) => async (dispatch: AppDispatch) => {
  try {
    dispatch(loadingStakeholder());
    if (id !== null && id !== undefined) {
      await APIService.put(`/stakeholders/${id}`, data).then((response) => {
        dispatch(resetStakeholder(nextAction));
      });
    } else {
      await APIService.post("/stakeholders", data).then((response) => {
        dispatch(resetStakeholder(nextAction));
      });
    }
  } catch (e) {
    // dispatch(setCustomerError());
  }
};

export const stakeholderDelete = (id: string) => async (dispatch: AppDispatch) => {
  try {
    await APIService.delete(`/stakeholders/${id}`).then((response) => {
    });
  } catch (e) {
  }
};
